import { Component } from '@angular/core';
import { NgbActiveModal, NotificationService } from '@agilent/ui-element';
import {
    ACApiService,
    WindowsUpdateImportAction
} from '@app/api/ac-api.service';
import { FormGroup } from '@angular/forms';
import Utils from "@app/utils/utils";

@Component({
    selector: 'app-windows-import-dialog',
    template: `
        <div role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="h2-title">Import Windows updates</h2>
                </div>
                <ag-dialog-content class="dialog-content">
                    <ag-file-selector
                      [label]="fileSelectLabel"
                      [filteredTypes]="fileSelectFilteredTypes"
                      [(ngModel)]="selectedFile"
                      (ngModelChange)="selectFile($event)"
                    ></ag-file-selector>
                    
                    <ng-container
                        *ngIf="showActionsTable()">
                        <div class="table-responsive ag-table">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>Action</th>
                                        <th>Version</th>
                                        <th>Status</th>
                                        <th class="no-wrap">KB Article/s</th>
                                        <th>Release Date</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody *ngFor="let action of actionsData">
                                    <td [ngClass]="{'add-text': action.action === 'add', 'update-text': action.action === 'update'}" class="text-uppercase">{{ action.action }}</td>
                                    <td [ngClass]="{'add-text': action.action === 'add', 'update-text': fieldChanged('version', action.changes)}">{{ action.update.version }}</td>
                                    <td [ngClass]="{'add-text': action.action === 'add', 'update-text': fieldChanged('status', action.changes)}" class="text-capitalize">{{ action.update.status }}</td>
                                    <td [ngClass]="{'add-text': action.action === 'add', 'update-text': fieldChanged('kbArticleIds', action.changes)}">{{ action.update.kbArticleIds }}</td>
                                    <td [ngClass]="{'add-text': action.action === 'add', 'update-text': fieldChanged('releaseDate', action.changes)}" class="no-wrap">{{ action.update.releaseDate | agTime }}</td>
                                    <td [ngClass]="{'add-text': action.action === 'add', 'update-text': fieldChanged('notes', action.changes)}">{{ action.update.notes }}</td>
                                </tbody>
                            </table>
                        </div>
                    </ng-container>
                    <app-user-reason-form
                        [hidden]="!showActionsTable()"
                        [disabled]="isSaving"
                        (formReady)="formIsReady($event)">
                    </app-user-reason-form>
                    
                    <div [hidden]="!isLoading">
                        <div-placeholder-animation
                            [heightValue]="'5rem'">
                        </div-placeholder-animation>
                        <div-placeholder-animation></div-placeholder-animation>
                    </div>
                </ag-dialog-content>
                <ag-dialog-actions>
                    <span *ngIf="isSaving">Please wait...</span>
                    <ag-loader
                        id="loading-spinner"
                        class="inline-progress"
                        [visible]="isSaving"
                        [small]="true"></ag-loader>
                    <ag-button
                        [disabled]="isLoading || isSaving"
                        type="secondary"
                        (click)="cancel()"
                        value="Cancel"
                        id="no-btn"></ag-button>
                    <ag-button
                        [disabled]="isImportButtonDisabled()"
                        [class.disabled]="isImportButtonDisabled()"
                        type="primary"
                        (click)="confirm()"
                        value="Import"
                        id="yes-btn"></ag-button>
                </ag-dialog-actions>
            </div>
        </div>
    `,
    styles: [
        `
            :host .inline-progress > ::ng-deep .loader-overlay {
                background: transparent !important;
                margin: 0;
                padding: 0;
                position: static;
                transform: scale(0.7);
            }

            .h2-title {
                font-size: 1.375rem;
                color: #53565A;
                margin-left: 20px;
                font-weight: 600;
                margin-top: 10px;
            }

            :host ::ng-deep .awf-icon {
                font-size: 40px;
            }

            .dialog-content {
                margin-left: 20px;
            }
            
            .content {
                display: flex;
                justify-content: space-between;
                margin: 1em auto;
            }
            
            :host ::ng-deep ag-collection .ag-table {
                position: relative !important;
            }
            
            :host .loader > ::ng-deep .loader-overlay {
              background: transparent !important;
              margin: 10px 0;
              padding: 0;
              position: static;
              transform: scale(1);
            }

            .add-text {
              color: green;
            }
            
            .update-text {
                color: orange;
            }
            
            .no-wrap {
                 white-space: nowrap;
            }
        `
    ]
})

export class WindowsImportDialogComponent {
    reasonForm: FormGroup = null;
    selectedFile: File = null;
    isLoading: boolean = false;
    isSaving: boolean = false;
    fileSelectLabel: string = 'Click here to select a file';
    importFileLimit: number = 2 * 1024 * 1024;
    fileSelectFilteredTypes: string[] = ['.winupd'];
    actionsData: WindowsUpdateImportAction[] = [];
    changesToImport: boolean = false;
    importActions: string[] = ["update", "add"];

    constructor(
        private acApiService: ACApiService,
        private activeModal: NgbActiveModal,
        private notificationService: NotificationService,
    ) {

    }

    async confirm() {
        if (this.isSaving) {
            return;
        }

        let importedWindowsUpdates = [];
        let skippedWindowsUpdates = [];
        try {
            this.isSaving = true;
            // Only send windows updates that we are updating or adding
            const updates = this.actionsData.filter(
                (actionsData) => this.importActions.includes(actionsData.action)).map(action => action.update);

            const reason = this.reasonForm.get('userReason').value;

            const importedResponse = await this.acApiService.importWindowsUpdates({
                windowsUpdates: updates,
                reason: reason
            });
            importedWindowsUpdates = importedResponse.windowsUpdates;
            skippedWindowsUpdates = importedResponse.skippedWindowsUpdates;
            const skippedWindowsVersions = skippedWindowsUpdates.map(update => update.version);
            const skippedWindowsVersionsString = skippedWindowsVersions.join(', ');

            if (importedWindowsUpdates.length !== 0) {
                this.notificationService.success('Windows updates imported');
            }

            if (skippedWindowsVersions.length !== 0) {
                this.notificationService.warning(`Windows update(s) '${skippedWindowsVersionsString}' already exist. Please reload the page`)
            }
        } catch (error) {
            const errorReason = error.data && error.data.error;
            if (errorReason === 'lastest_available_windows_update_cannot_be_modified') {
                this.notificationService.error("Cannot modify the lastest available released windows update")
            }
            else {
                this.notificationService.error('Windows update import failed');
            }
        } finally {
            this.isSaving = false;
            this.activeModal.close(importedWindowsUpdates);
        }
    }

    cancel() {
        this.activeModal.dismiss();
    }

    formIsReady(form: FormGroup) {
        this.reasonForm = form;
    }

    /**
     * Import button is disabled if:
     *  - actionsData is not populated,
     *  - reasonForm is invalid / has not been touched,
     *  - currently loading resources.
     */
    isImportButtonDisabled() {
        return (
            this.actionsData.length == 0 ||
            !this.changesToImport ||
            !this.reasonForm.dirty ||
            this.reasonForm.invalid ||
            this.isLoading
        );
    }

    selectFile(file) {
        this.isLoading = true;
        this.changesToImport = false;
        this.actionsData = [];
        this.reasonForm.reset()

        // File was unselected / cleared
        if (file === null) {
            this.isLoading = false;
            return;
        }

        // Validate file extension
        const fileExtension = file.name.slice((file.name.lastIndexOf(".") - 1 >>> 0) + 2);
        if (fileExtension !== 'winupd') {
            this.notificationService.error('Invalid file format');
            this.isLoading = false;
            return;
        }

        // File is larger than the import file limit
        if (file.size && file.size > this.importFileLimit) {
            this.notificationService.error('File is too large to process');
            this.isLoading = false;
            return;
        }

        // Read the file as text and process it
        Utils.getFileContentAsText(file).then((fileContent) => {
            this.processFileContent(fileContent);
        })
    }

    private async processFileContent(fileContentString) {
        try {
            let fileContent = null;

            try {
                fileContent = JSON.parse(fileContentString);
                this.validateFileContent(fileContent);
            } catch (error) {
                switch (error)  {
                    case 'version-validation-error':
                        this.notificationService.error('The version is either missing or in incorrect format');
                        break;
                    case 'records-validation-error':
                        this.notificationService.error('Records are either missing or in incorrect format');
                        break;
                    case 'records-format-error':
                        this.notificationService.error('Records are either missing or in incorrect format');
                        break;
                    default:
                        this.notificationService.error('Error processing the import file');
                        break;
                }

                this.isLoading = false;
                return;
            }

            // Get the Windows Updates in corrected format
            const windowsUpdates = this.getWindowsUpdates(fileContent);
            const version = fileContent["version"];

            if (windowsUpdates.length == 0) {
                this.notificationService.error('Records are empty');
                this.isLoading = false;
                return;
            }

            // Get the Import Actions for the supplied Windows Updates
            const importActions = await this.acApiService.fetchWindowsUpdateImportActions({
                records: windowsUpdates,
                version: version
            }).catch(reason => {
                this.notificationService.error(Utils.parserServerError(reason.data.error));
                this.isLoading = false;
                return;
            });

            this.changesToImport = importActions.importActions.some(item => this.importActions.includes(item.action));
            this.actionsData = importActions.importActions;
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Validates the import file content:
     *  - 'version' key exists and is of type 'string',
     *  - 'records' key exists and contains a list of objects.
     *
     * @param fileContent Content of the file (JSON)
     * @private
     */
    private validateFileContent(fileContent) {
        // Validate 'version'
        if (fileContent.version === undefined || typeof fileContent.version !== "string") {
            throw "version-validation-error";
        }

        // Validate 'records'
        if (fileContent.records === undefined || !Array.isArray(fileContent.records) || !fileContent.records.every(record => typeof record === 'object')) {
            throw "records-validation-error";
        }

        fileContent.records.forEach(record => {
            if (!record.version || !record.status || !record.kbArticleIds || !record.releaseDate) {
                throw "records-format-error"
            }
        })
    }

    /**
     * Returns list of Windows Updates with these changes:
     *  - status is changed to all lowercase.
     *
     * @param fileContent Content of the file (JSON)
     * @private
     */
    private getWindowsUpdates(fileContent) {
        return fileContent.records.map((record: any) => {
            return {
                version: record.version,
                status: record.status.toLowerCase(),
                kbArticleIds: record.kbArticleIds,
                releaseDate: record.releaseDate,
                notes: record.notes
            };
        });
    }

    showActionsTable() {
        return this.actionsData.length > 0 && !this.isLoading;
    }

    /**
     * Returns the state of the field - weather or not the requested action changes the
     * Windows Update's field.
     *
     * @param fieldName WindowsImportAction field name (status, version, ..)
     * @param changes List of changes ([status, version, ..])
     */
    fieldChanged(fieldName, changes) {
        return !!(changes && changes.length > 0 && changes.includes(fieldName));
    }
}
